import { CaretRightOutlined, PauseOutlined } from '@ant-design/icons'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type PointerEvent,
} from 'react'
import { useCrmIntegrationVoiceRecorder } from '../../hooks/useCrmIntegrationVoiceRecorder'
import classes from '../../styles/crmIntegrationChat.module.css'
import recorderClasses from '../../styles/crmIntegrationVoiceRecorder.module.css'

const formatPlayerTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00'
  }

  const totalSec = Math.floor(seconds)
  const minutes = Math.floor(totalSec / 60)
  const secs = totalSec % 60
  return `${minutes}:${String(secs).padStart(2, '0')}`
}

export type CrmIntegrationVoiceRecorderHandle = {
  getVoiceFile: () => File | null
  getPreviewUrl: () => string
  getDurationSec: () => number
  reset: () => void
}

type CrmIntegrationVoiceRecorderProps = {
  disabled?: boolean
  onRecordingStateChange?: (state: {
    isRecording: boolean
    hasAudio: boolean
    isVoiceReady: boolean
  }) => void
}

const CrmIntegrationVoiceRecorder = forwardRef<
  CrmIntegrationVoiceRecorderHandle,
  CrmIntegrationVoiceRecorderProps
>(({ disabled = false, onRecordingStateChange }, ref) => {
  const {
    isRecording,
    audioURL,
    audioFile,
    startRecording,
    stopRecording,
    resetRecording,
    cleanup,
    hasAudio,
  } = useCrmIntegrationVoiceRecorder()

  const [elapsedSec, setElapsedSec] = useState(0)
  const [recordError, setRecordError] = useState<string | null>(null)
  const recordingStartedAtRef = useRef<number | null>(null)
  const recorderApiRef = useRef({
    startRecording,
    stopRecording,
    resetRecording,
    cleanup,
  })
  recorderApiRef.current = {
    startRecording,
    stopRecording,
    resetRecording,
    cleanup,
  }

  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isSeeking, setIsSeeking] = useState(false)

  const isVoiceReady = hasAudio && Boolean(audioFile) && !isRecording

  useImperativeHandle(
    ref,
    () => ({
      getVoiceFile: () => audioFile,
      getPreviewUrl: () => audioURL,
      getDurationSec: () =>
        hasAudio ? (duration > 0 ? duration : elapsedSec) : 0,
      reset: () => recorderApiRef.current.resetRecording(),
    }),
    [audioFile, audioURL, duration, elapsedSec, hasAudio],
  )

  useEffect(() => {
    if (disabled) {
      return
    }

    let cancelled = false

    void recorderApiRef.current.startRecording().catch(() => {
      if (!cancelled) {
        setRecordError('Не удалось начать запись. Проверьте доступ к микрофону.')
      }
    })

    return () => {
      cancelled = true
      recorderApiRef.current.cleanup()
      recorderApiRef.current.resetRecording()
    }
  }, [disabled])

  useEffect(() => {
    onRecordingStateChange?.({
      isRecording,
      hasAudio,
      isVoiceReady,
    })
  }, [hasAudio, isRecording, isVoiceReady, onRecordingStateChange])

  useEffect(() => {
    if (!isRecording) {
      recordingStartedAtRef.current = null
      return
    }

    recordingStartedAtRef.current = Date.now()
    setElapsedSec(0)

    const timerId = window.setInterval(() => {
      const startedAt = recordingStartedAtRef.current
      if (startedAt == null) {
        return
      }
      setElapsedSec((Date.now() - startedAt) / 1000)
    }, 200)

    return () => {
      window.clearInterval(timerId)
    }
  }, [isRecording])

  useEffect(() => {
    if (!hasAudio || !audioURL) {
      setIsPlaying(false)
      setCurrentTime(0)
      setDuration(0)
      return
    }

    setDuration(elapsedSec)
    setCurrentTime(0)
    setIsPlaying(false)
  }, [audioURL, elapsedSec, hasAudio])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !hasAudio) {
      return
    }

    const handleTimeUpdate = () => {
      if (!isSeeking) {
        setCurrentTime(audio.currentTime)
      }
    }

    const handleLoadedMetadata = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration)
      }
    }

    const handleEnded = () => {
      setIsPlaying(false)
      setCurrentTime(0)
      audio.currentTime = 0
    }

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [audioURL, hasAudio, isSeeking])

  const togglePlay = useCallback(() => {
    const audio = audioRef.current
    if (!audio) {
      return
    }

    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
      return
    }

    void audio
      .play()
      .then(() => {
        setIsPlaying(true)
      })
      .catch(() => {
        setIsPlaying(false)
      })
  }, [isPlaying])

  const handleSeekChange = (event: ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current
    if (!audio || duration <= 0) {
      return
    }

    const nextPercent = Number(event.target.value)
    const nextTime = (nextPercent / 100) * duration
    audio.currentTime = nextTime
    setCurrentTime(nextTime)
  }

  const handleSeekPointerDown = (event: PointerEvent<HTMLInputElement>) => {
    event.stopPropagation()
    setIsSeeking(true)
  }

  const handleSeekPointerUp = () => {
    setIsSeeking(false)
  }

  const handleStopRecording = () => {
    recorderApiRef.current.stopRecording()
  }

  const progressPercent =
    duration > 0 ? Math.min((currentTime / duration) * 100, 100) : 0

  const leftTimeLabel = isRecording
    ? formatPlayerTime(elapsedSec)
    : formatPlayerTime(isPlaying || currentTime > 0 ? currentTime : duration)

  if (recordError) {
    return (
      <div className={recorderClasses.root} role="alert">
        <p className={recorderClasses.error}>{recordError}</p>
      </div>
    )
  }

  return (
    <div className={recorderClasses.root}>
      <div className={classes.bubbleVoiceCard}>
        <div className={classes.voicePlayer}>
          <span
            className={`${classes.voicePlayerTime} ${recorderClasses.time}`}
          >
            {leftTimeLabel}
          </span>

          {isRecording ? (
            <button
              type="button"
              className={classes.voicePlayerPlayButton}
              aria-label="Остановить запись"
              disabled={disabled}
              onClick={handleStopRecording}
            >
              <span className={recorderClasses.stopIcon} aria-hidden />
            </button>
          ) : hasAudio ? (
            <button
              type="button"
              className={classes.voicePlayerPlayButton}
              aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
              disabled={disabled}
              onClick={togglePlay}
            >
              {isPlaying ? (
                <PauseOutlined
                  className={classes.voicePlayerPlayIcon}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              ) : (
                <CaretRightOutlined
                  className={classes.voicePlayerPlayIcon}
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              )}
            </button>
          ) : null}

          <input
            type="range"
            className={classes.voicePlayerRange}
            min={0}
            max={100}
            step={0.1}
            value={hasAudio ? progressPercent : 0}
            disabled={!hasAudio || duration <= 0 || disabled}
            aria-label="Позиция воспроизведения"
            style={{
              background: hasAudio
                ? `linear-gradient(to right, #835499 ${progressPercent}%, #e8e8e8 ${progressPercent}%)`
                : '#e8e8e8',
            }}
            onChange={handleSeekChange}
            onPointerDown={handleSeekPointerDown}
            onPointerUp={handleSeekPointerUp}
            onPointerLeave={handleSeekPointerUp}
            onClick={(event) => event.stopPropagation()}
          />

          {hasAudio && audioURL ? (
            <audio
              ref={audioRef}
              src={audioURL}
              preload="metadata"
              className={classes.voicePlayerAudio}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
})

CrmIntegrationVoiceRecorder.displayName = 'CrmIntegrationVoiceRecorder'

export default CrmIntegrationVoiceRecorder
